import { useState } from "react";
// @mui
import {
  Stack,
  Button,
  Typography,
  Box,
  Card,
  Radio,
  CardContent,
} from '@mui/material';
// components
import Iconify from '../../components/Iconify';
import SvgIconStyle from '../../components/SvgIconStyle';
import FeeTaxDialog from "./FeeTaxDialog";
// ----------------------------------------------------------------------

export default function CompanySelectedPlan({ offer }) {
  const plan = offer?.trial_selected ? 'trial' : offer?.longterm_selected ? 'longterm' : '4weeks';
  const allowLongterm = offer?.longterm_salary > 0 && offer?.longterm_working_hours_per_week > 0;
  const [isOpenFeeTaxDialog, setIsOpenFeeTaxDialog] = useState(false);

  return (
    <Card sx={{ mb: 3 }}>
      <CardContent>
        <Stack spacing={1}>
          <Stack sx={{ mb: 2 }} spacing={.5}>
            <Typography variant="h6">
              회사가 선택한 계약 플랜
            </Typography>
            <Typography>
              지금 선택된 계약 플랜은 최종 결정된 플랜이 아닙니다. 계약 플랜은 미팅때 협의하여 변경할 수 있어요.
            </Typography>
          </Stack>

          <Stack sx={{ width: 1 }} direction={{ xs: "column", sm: "column", md: "row" }} alignItems="flex-start" spacing={2}>
            {/* 4주 플랜 */}
            <Card
              sx={{
                width: 1,
                borderRadius: 2,
                border: "1px solid rgba(145, 158, 171, 0.24)",
                boxShadow: "none",
                ...(plan === "4weeks") && {
                  backgroundColor: "grey.200",
                  border: (theme) => `2px solid ${theme.palette.secondary.main}`,
                },
              }}
            >
              <Box sx={{ p: 3, pt: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle1">
                      {allowLongterm ? "4주 정식 계약" : "4주 단위 계약"}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: "text.disabled" }}>
                      플랜
                    </Typography>
                  </Stack>
                  <Radio color="secondary" value="4weeks" checked={plan==="4weeks"} />
                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="h4" color={plan==="4weeks" ? "secondary.main" : "text.secondary"}>{(offer.salary_for_4weeks).toLocaleString()}만원</Typography>
                  <Typography variant="h6" color={plan==="4weeks" ? "secondary.main" : "text.secondary"}>/ 4주</Typography>
                </Stack>

                <Stack direction="row" spacing={.5} alignItems="center">
                  <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ width:20, height: 20, color: plan==="4weeks" ? "secondary.main" : "text.disabled" }} />
                  <Typography variant="subtitle2" sx={{ color: plan==="4weeks" ? "secondary.main" : "text.secondary" }}>
                    주 {offer.working_hours_per_week}시간
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    근무
                  </Typography>
                </Stack>
              </Box>
            </Card>

            {/* 2주 trial */}
            <Card
              sx={{
                width: 1,
                borderRadius: 2,
                border: "1px solid rgba(145, 158, 171, 0.24)",
                boxShadow: "none",
                ...(plan === "trial") && {
                  backgroundColor: "grey.200",
                  border: (theme) => `2px solid ${theme.palette.secondary.main}`,
                },
              }}
            >
              <Box sx={{ p: 3, pt: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle1">
                      {allowLongterm ? "2주 체험판" : "2주 50만원 체험판"}
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: "text.disabled" }}>
                      플랜
                    </Typography>
                  </Stack>
                  <Radio color="secondary" value="trial" checked={plan==="trial"} />
                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="h4" color={plan==="trial" ? "secondary.main" : "text.secondary"}>{(offer.trial_salary).toLocaleString()}만원</Typography>
                  <Typography variant="h6" color={plan==="trial" ? "secondary.main" : "text.secondary"}>/ 2주</Typography>
                </Stack>

                <Stack direction="row" spacing={.5} alignItems="center">
                  <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ width:20, height: 20, color: plan==="trial" ? "secondary.main" : "text.disabled" }} />
                  <Typography variant="subtitle2" sx={{ color: plan==="trial" ? "secondary.main" : "text.secondary" }}>
                    주 {offer.trial_working_hours_per_week}시간
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    근무
                  </Typography>
                </Stack>
              </Box>
            </Card>

            {/* 8주 trial */}
            {allowLongterm && (
            <Card
              sx={{
                width: 1,
                borderRadius: 2,
                border: "1px solid rgba(145, 158, 171, 0.24)",
                boxShadow: "none",
                ...(plan === "longterm") && {
                  backgroundColor: "grey.200",
                  border: (theme) => `2px solid ${theme.palette.secondary.main}`,
                },
              }}
            >
              <Box sx={{ p: 3, pt: 2 }}>
                <Stack direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle1">
                      8주 장기 계약
                    </Typography>
                    <Typography variant="subtitle2" sx={{ color: "text.disabled" }}>
                      플랜
                    </Typography>
                  </Stack>
                  <Radio color="secondary" value="longterm" checked={plan==="longterm"} />
                </Stack>

                <Stack direction="row" spacing={0.5} alignItems="center">
                  <Typography variant="h4" color={plan==="longterm" ? "secondary.main" : "text.secondary"}>{(offer.longterm_salary).toLocaleString()}만원</Typography>
                  <Typography variant="h6" color={plan==="longterm" ? "secondary.main" : "text.secondary"}>/ 8주</Typography>
                </Stack>

                <Stack direction="row" spacing={.5} alignItems="center">
                  <SvgIconStyle src={`/icons/ic_clock.svg`} sx={{ width:20, height: 20, color: plan==="longterm" ? "secondary.main" : "text.disabled" }} />
                  <Typography variant="subtitle2" sx={{ color: plan==="longterm" ? "secondary.main" : "text.secondary" }}>
                    주 {offer.longterm_working_hours_per_week}시간
                  </Typography>
                  <Typography variant="body2" sx={{ color: "text.secondary" }}>
                    근무
                  </Typography>
                </Stack>
              </Box>
            </Card>
            )}
          </Stack>
        </Stack>

        <Button
          size="small"
          sx={{
            mt: 2,
            maxWidth: 'max-content',
            color: 'text.secondary',
          }}
          fullWidth={false}
          startIcon={<Iconify icon={'eva:info-fill'} />}
          onClick={() => setIsOpenFeeTaxDialog(true)}
        >
          수수료 & 세금 정책
        </Button>
      </CardContent>
      <FeeTaxDialog isOpen={isOpenFeeTaxDialog} close={() => setIsOpenFeeTaxDialog(false)} />
    </Card>
  )
}